import React, { PureComponent } from 'react';
import {
    Coordination,
    CoordinationState,
    DetailedSolution,
    RegulationImpact,
    ReroutingImpact,
    Solution,
    SolutionState,
    SolutionType,
    TVTimePeriodSelection,
} from 'app/model/SOL49';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { Button, Divider, notification, Popconfirm, Popover, Space, Spin, Tabs } from 'antd';
import { Flex } from 'app/components/common/Flex';
import './CoordinationPanel.less';
import { SINGLE_SELECTION, SingleSelection } from 'app/common/table/ManagedTable';
import { SolutionSummary } from '../solver/SolutionSummaryPanel';
import {
    CapacityModel,
    MeasureCreateRequestDto,
    MeasureDto,
    MeasureId,
    RegulationOrMcdmOnlyDto,
    RegulationProposalAcceptRejectDto,
    ReroutingDto,
} from 'app/model/CORE';
import memoizeOne from 'memoize-one';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { ReroutingImpactPanel } from '../solver/ReroutingImpactPanel';
import { SolutionMeasures } from '../solver/SolutionMeasures';
import { TvLoadImpact } from 'app/components/panels/solver/TvLoadImpact';
import { RegulationImpactPanel } from 'app/components/panels/solver/RegulationImpactPanel';
import { SolutionUtils } from 'app/common/util/SolutionUtils';
import { ModalPanelProps } from 'app/components/panels/DashboardModal';
import { CoordinationChat } from 'app/components/panels/coordination/CoordinationChat';
import { CoordinationStatusPanel } from 'app/components/panels/coordination/CoordinationStatusPanel';
import AppContext from 'app/common/context/AppContext';
import {
    CoordinationSummaryUpdateEvent,
    PartialDashboardDataUpdateEvent,
    ShowModalEvent,
} from 'app/common/eventbus/Events';
import { ApplySolutionConfirmationPanel } from 'app/components/panels/coordination/ApplySolutionConfirmationPanel';
import { FlexTabs } from 'app/components/common/FlexTabs';
import ConditionalRender from 'app/common/components/ConditionalRender';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import Utils from 'app/common/util/Utils';
import { TargetTime } from 'app/components/panels/coordination/TargetTime';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import { DeltaTvImpact } from '../solver/DeltaTvImpact';
import { DeltaMeasuresImpact } from '../solver/DeltaMeasuresImpact';
import { CoordinationStatusType, EntityType } from 'app/model/client/SOL49';

interface CoordinationPanelState {
    coordination: Coordination;
    loading: boolean;
    selectedSolution: string;
    selectedMeasure: string;
    selectedFMP: string;
    outdatedSolutions: Array<string>;
}

interface CoordinationPanelProps extends ModalPanelProps {
    coordinationId: string;
    onCoordinationUpdated: (Coordination) => void;
}

export class CoordinationPanel extends PureComponent<CoordinationPanelProps, CoordinationPanelState> {
    private timer;

    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            coordination: undefined,
            selectedSolution: undefined,
            selectedMeasure: undefined,
            selectedFMP: undefined,
            outdatedSolutions: [],
        };
    }

    componentDidMount() {
        // Init coordination
        this.fetchCoordination(coordination => {
            const defaultSolution = coordination.solutions.find(s => s.type !== SolutionType.INITIAL);
            const defaultFmp = SolutionUtils.getImpactedFMPs(defaultSolution)[0];
            this.setState({
                coordination: coordination,
                selectedSolution: defaultSolution.name,
                selectedMeasure: this.getMeasureForFmp(defaultSolution, defaultFmp),
                selectedFMP: defaultFmp,
            });
        });

        // TODO Synchronise with Chat and Status panels !
        // Periodically check for new version
        this.timer = setInterval(() => this.getCoordinationVersion(), 5000);
    }

    componentWillUnmount(): void {
        clearInterval(this.timer);
    }

    private getCoordinationVersion = () => {
        ServiceFactory.sol49Service()
            .getCoordinationVersion(this.props.coordinationId)
            .then(reply => {
                if (reply.error) {
                    notification.error({
                        message: "Error while fetching coordination's version",
                        description: reply.error + '',
                    });
                } else if (reply.data > this.state.coordination?.version) {
                    // Refresh the coordination
                    this.fetchCoordination(coordination =>
                        this.setState({
                            coordination: coordination,
                        }),
                    );
                }
            })
            .catch(err => {
                notification.error({
                    message: "Error while fetching coordination's version",
                    description: err,
                });
            });
    };

    private fetchCoordination = (onSuccess: (coordination: Coordination) => void) => {
        this.setState({
            loading: true,
        });
        ServiceFactory.sol49Service()
            .getCoordination(this.props.coordinationId)
            .then(reply => {
                if (reply.error) {
                    notification.error({
                        message: 'Error while fetching coordination',
                        description: reply.error + '',
                    });
                } else {
                    onSuccess(reply.data);
                }
            })
            .catch(err => {
                notification.error({
                    message: 'Error while fetching coordination',
                    description: '' + err,
                });
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    };

    private getMeasureForFmp = (solution: DetailedSolution, fmp: string): string => {
        const measureImpacts = Object.fromEntries(
            Object.entries(solution.solutionImpact.measureImpacts).filter(([_, mI]) =>
                SolutionUtils.isInitialMeasureImpact(mI),
            ),
        );
        return solution.measures.find(m => measureImpacts[m.measureId.id].fmpUnit === fmp).measureId.id;
    };

    private getApprovedSolutions(): string[] {
        const approvedSolutions = new Set<string>();
        this.state.coordination?.solutions.forEach(solution => {
            for (const fmp of Object.keys(this.state.coordination.status)) {
                const state = this.state.coordination.status[fmp][solution.name];
                if (state === SolutionState.APPROVED) {
                    approvedSolutions.add(solution.name);
                }
            }
        });
        return Array.from(approvedSolutions);
    }

    selection = memoizeOne(
        (selectedMeasure: MeasureDto): SingleSelection<MeasureDto> => ({
            selectionMode: SINGLE_SELECTION,
            onSelectionChange: newSelection => this.setState({ selectedMeasure: newSelection.measureId.id }),
            selectedItem: selectedMeasure,
        }),
    );

    /** HANDLERS **/
    showApplyConfirmation = () => {
        const approvedSolutions = this.getApprovedSolutions();
        const defaultSolution = approvedSolutions.includes(this.state.selectedSolution)
            ? this.state.selectedSolution
            : approvedSolutions[0];
        const content = (
            <ApplySolutionConfirmationPanel
                solutions={Array.from(approvedSolutions)}
                defaultSolution={defaultSolution}
                onApplySolution={this.applySolution}
            />
        );
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '35em'));
    };

    handleStatusChange = (newStatus: CoordinationStatusType) => {
        if (this.state.coordination) {
            const newCoordination = { ...this.state.coordination, status: newStatus };
            this.setState(
                {
                    coordination: newCoordination,
                },
                () => {
                    this.props.onCoordinationUpdated(this.state.coordination);
                    CoordinationSummaryUpdateEvent.fireEvent(newCoordination);
                },
            );
        }
    };

    handleMeasureEdited = (measureId: MeasureId, measure: MeasureDto) => {
        const solution = this.state.coordination.solutions.find(s => s.name === this.state.selectedSolution);
        // Editing proposed solution, append "AMENDED" to proposal note for later if proposal is accepted !
        if (SolutionUtils.isInitialSolution(solution) && MeasureUtils.isRegulationProposal(measure)) {
            if (!measure.proposalNote || !measure.proposalNote.includes('AMENDED')) {
                const newNote = (measure.proposalNote ? measure.proposalNote + ';' : '') + 'AMENDED';
                measure = Object.assign({}, measure, { proposalNote: newNote });
            }
        }
        const updatedMeasures = solution.measures.map(m => (m.measureId.id === measureId.id ? measure : m));
        const updatedSolution = { ...solution, measures: updatedMeasures };
        const updatedSolutions = this.state.coordination.solutions.map(s =>
            s.name === this.state.selectedSolution ? updatedSolution : s,
        );
        const updatedCoordination = { ...this.state.coordination, solutions: updatedSolutions };

        // Mark solution as outdated (needs to be updated)
        const outdatedSolutions = this.state.outdatedSolutions.slice();
        Utils.addIfNeeded(outdatedSolutions, this.state.selectedSolution);

        this.setState({
            coordination: updatedCoordination,
            outdatedSolutions: outdatedSolutions,
        });
    };

    updateSolution = () => {
        this.setState({
            loading: true,
        });

        const detailedSolution = this.state.coordination.solutions.find(s => s.name === this.state.selectedSolution);
        const solution: Solution = {
            measures: detailedSolution.measures,
            name: detailedSolution.name,
            tvMatchingKind: detailedSolution.tvMatchingKind,
            tvs: detailedSolution.tvs,
            type: detailedSolution.type,
        };
        const tvAndPeriods: TVTimePeriodSelection[] = detailedSolution.solutionImpact.tvImpact.map(tvImpact => ({
            timePeriod: tvImpact.timePeriod,
            tvId: tvImpact.tvId,
            selected: true,
        }));
        const monitoredPeriod = MonitoredLoadUtils.getMonitoredPeriod(
            detailedSolution.solutionImpact.tvImpact[0].monitoredTv.loadValues,
        );

        // Compute new solution's impact, then update coordination and finally post a new chat message
        ServiceFactory.sol49Service()
            .getDetailedSolutions({
                solutions: [solution],
                capacityModel: CapacityModel.CCM,
                tvAndPeriods: tvAndPeriods,
                monitoredPeriod: monitoredPeriod,
                dataset: AppContext.getDataset(),
            })
            .then(reply => {
                const updatedSolutions = this.state.coordination.solutions.map(s =>
                    s.name === this.state.selectedSolution ? reply.data.solutions[0] : s,
                );
                return { ...this.state.coordination, solutions: updatedSolutions };
            })
            .then(coordination => {
                return ServiceFactory.sol49Service().updateCoordination(coordination);
            })
            .then(reply => {
                this.setState({
                    coordination: reply.data,
                    outdatedSolutions: Utils.copyAndRemoveIfNeeded(
                        this.state.outdatedSolutions,
                        this.state.selectedSolution,
                    ),
                });
            })
            .then(() =>
                ServiceFactory.technicalService().postChatMessage({
                    message: 'UPDATED SOLUTION: ' + this.state.selectedSolution,
                    toId: this.state.coordination.id,
                    fromId: AppContext.configData.claimedUserId,
                }),
            )
            .catch(err => {
                console.log("Error updating detailedSolution's impact:", err);
                notification.error({ message: "Error updating detailedSolution's impact", description: err });
            })
            .finally(() => this.setState({ loading: false }));
    };

    /** Apply solution, accepting its measures **/
    private applySolution = async (solutionName: string) => {
        this.setState({ loading: true });

        const solution = this.state.coordination.solutions.find(s => s.name === solutionName);
        const proposedSolution = SolutionUtils.getInitialSolution(this.state.coordination.solutions);
        const appliedSolutions = [];

        // The solution to apply is not the proposed nor the CUSTOM one, so we reject the proposed solution
        if (proposedSolution && proposedSolution.name !== solutionName && !SolutionUtils.isCustomSolution(solution)) {
            for (const proposedMeasure of proposedSolution.measures) {
                // TODO reject proposed reroutings ??
                if (MeasureUtils.isRegulationProposal(proposedMeasure)) {
                    proposedMeasure.noDelayWindowInMs = -1;
                    proposedMeasure.proposalNote = proposedMeasure.proposalNote
                        ? proposedMeasure.proposalNote
                        : '' +
                          '; USER(' +
                          AppContext.configData.claimedUserId +
                          ')' +
                          '; ALT(' +
                          solution.measures.map(m => m.measureId.id).join(',') +
                          ')';
                    const reply = await ServiceFactory.stamCoreService().acceptRejectRegulationProposal({
                        acceptReject: RegulationProposalAcceptRejectDto.REJECT,
                        dataset: AppContext.getDataset(),
                        regulationProposal: proposedMeasure,
                    });

                    if (reply.error) {
                        console.error('Error rejecting proposal: ', reply.error);
                        notification.error({
                            message: 'Error',
                            description: (
                                <>
                                    {reply.error.message} <br />
                                    {reply.error.details}
                                </>
                            ),
                            duration: 0,
                        });
                    }
                } else {
                    console.error("Can't reject measure which is not a regulation proposal: ", proposedMeasure);
                }
            }
        }

        for (const measure of solution.measures) {
            try {
                let reply;
                if (MeasureUtils.isRegulationProposal(measure) && AppContext.isNmvpBaseline()) {
                    // TODO fix serverSide, NoDelayWindow should be null in CREATE/UPDATE ctx
                    measure.noDelayWindowInMs = -1;
                    measure.proposalNote = measure.proposalNote
                        ? measure.proposalNote
                        : '' +
                          '; USER(' +
                          AppContext.configData.claimedUserId +
                          ')' +
                          `${SolutionUtils.isCustomSolution(solution) ? 'AMENDED' : ''}`;
                    reply = await ServiceFactory.stamCoreService().acceptRejectRegulationProposal({
                        acceptReject: RegulationProposalAcceptRejectDto.ACCEPT,
                        dataset: AppContext.getDataset(),
                        regulationProposal: measure,
                    });
                } else {
                    reply = await ServiceFactory.stamCoreService().createMeasure({
                        computeNetworkImpactAssessment: false,
                        dataset: AppContext.getDataset(),
                        measure: measure,
                        waitUntilApplied: true,
                    } as MeasureCreateRequestDto);
                }

                if (reply.error) {
                    notification.error({
                        message: 'Error',
                        description: (
                            <>
                                {reply.error.message} <br />
                                {reply.error.details}
                            </>
                        ),
                        duration: 0,
                    });
                } else {
                    appliedSolutions.push(reply.data);
                }
            } catch (reason) {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            }
        }

        // Update the state of measure proposals
        PartialDashboardDataUpdateEvent.fireEvent(EntityType.MEASURE_PROPOSAL);
        console.log(`${appliedSolutions.length}/${solution.measures.length} measures applied successfully`);
        if (appliedSolutions.length === solution.measures.length) {
            // Set coordination's state to APPLIED and send new chat message
            const coordination = { ...this.state.coordination, state: CoordinationState.APPLIED };
            ServiceFactory.sol49Service()
                .updateCoordination(coordination)
                .then(reply => {
                    this.setState({ coordination: reply.data }, () =>
                        CoordinationSummaryUpdateEvent.fireEvent(reply.data),
                    );
                })
                .then(() => {
                    return ServiceFactory.technicalService().postChatMessage({
                        message: 'APPLIED SOLUTION: ' + solution.name,
                        fromId: AppContext.configData.claimedUserId,
                        toId: this.props.coordinationId,
                        timestamp: AppContext.getCurrentTime(),
                    });
                })
                .catch(err => {
                    notification.error({
                        message: 'Error applying coordination',
                        description: err,
                    });
                })
                .finally(() => this.setState({ loading: false }));
        } else {
            this.setState({ loading: false });
        }
    };

    /** Reset coordination's state to last saved, used to overwrite measure changes **/
    resetCoordination = () => {
        this.fetchCoordination(coordination => {
            this.setState({
                coordination: coordination,
                outdatedSolutions: Utils.copyAndRemoveIfNeeded(
                    this.state.outdatedSolutions,
                    this.state.selectedSolution,
                ),
            });
        });
    };

    cancelCoordination = () => {
        this.setState({
            loading: true,
        });

        const coordination = { ...this.state.coordination, state: CoordinationState.CANCELLED };
        ServiceFactory.sol49Service()
            .updateCoordination(coordination)
            .then(reply => {
                this.setState({ coordination: reply.data }, () => CoordinationSummaryUpdateEvent.fireEvent(reply.data));
            })
            .then(() => {
                return ServiceFactory.technicalService().postChatMessage({
                    message: 'HAS CANCELLED COORDINATION',
                    fromId: AppContext.configData.claimedUserId,
                    toId: this.props.coordinationId,
                    timestamp: AppContext.getCurrentTime(),
                });
            })
            .catch(err =>
                notification.error({
                    message: 'Error to cancel the coordination',
                    description: err,
                }),
            )
            .finally(() => {
                this.setState({ loading: false });
            });
    };

    /** RENDERING **/
    render() {
        const terminated = this.state.coordination?.state !== CoordinationState.ONGOING;
        const approvedSolutions = this.getApprovedSolutions();

        return (
            <Spin spinning={this.state.loading}>
                <Flex direction="vertical" className="coordination-panel">
                    <Flex direction="horizontal">
                        <Flex size="60%" className="content">
                            <Flex direction="vertical">
                                <Flex className="header" size="5em">
                                    <Flex direction="horizontal">
                                        <Flex direction="vertical" style={{ justifyContent: 'center' }}>
                                            <span className="name">
                                                Coordination: {this.state.coordination?.owner.toUpperCase()}-
                                                {this.state.coordination?.name}
                                            </span>
                                            <span className="state">{this.state.coordination?.state}</span>
                                        </Flex>
                                        <ConditionalRender
                                            condition={Utils.isExists(this.state.coordination?.targetTime)}>
                                            <TargetTime targetTime={this.state.coordination?.targetTime} />
                                        </ConditionalRender>
                                    </Flex>
                                </Flex>
                                {this.renderSolutions()}
                                <ConditionalRender condition={AuthorisationUtils.isNmUser()}>
                                    <CoordinationPanelButtons
                                        onApplySolution={this.showApplyConfirmation}
                                        applySolutionDisabled={Utils.isEmpty(approvedSolutions) || terminated}
                                        onClose={this.props.onClose}
                                        onResetCoordination={this.resetCoordination}
                                        onCancelCoordination={this.cancelCoordination}
                                        cancelCoordinationDisabled={terminated}
                                        onUpdateSolution={this.updateSolution}
                                        isOutdated={this.state.outdatedSolutions.includes(this.state.selectedSolution)}
                                    />
                                </ConditionalRender>
                            </Flex>
                        </Flex>
                        <Flex className="divider-container" size="5%">
                            <Divider className="divider" type="vertical" />
                        </Flex>
                        <Flex size="35%" className="chat-and-status overflow" shrink={false} direction="vertical">
                            <CoordinationStatusPanel
                                coordinationId={this.props.coordinationId}
                                onStatusChange={this.handleStatusChange}
                                selectedSolution={this.state.selectedSolution}
                                selectedFmp={this.state.selectedFMP}
                                disabled={terminated}
                            />
                            <CoordinationChat coordinationId={this.props.coordinationId} disabled={terminated} />
                        </Flex>
                    </Flex>
                </Flex>
            </Spin>
        );
    }

    renderSolutions = () => {
        const solutions = this.state.coordination?.solutions;
        if (!solutions) return null;

        return (
            <FlexTabs
                activeKey={this.state.selectedSolution}
                type="card"
                onTabClick={(key: string) => {
                    const solution = solutions.find(s => s.name === key);
                    return this.setState({
                        selectedSolution: key,
                        selectedFMP: SolutionUtils.getImpactedFMPs(solution)[0],
                        selectedMeasure: solution.measures[0].measureId.id,
                    });
                }}
                items={solutions.map(solution => ({
                    key: solution.name,
                    label: solution.name,
                    children: this.renderSolution(solution),
                }))}
            />
        );
    };

    private renderSolution = (solution: DetailedSolution) => {
        const impactedFmps = SolutionUtils.getImpactedFMPs(solution);
        const isOutdated = this.state.outdatedSolutions.includes(solution.name);
        const className = `solution-info ${isOutdated ? 'outdated' : ''}`;
        return (
            <Flex className={className} direction="vertical">
                <Flex>
                    <div className="solution-summary">
                        <SolutionSummary solution={solution} />
                    </div>
                    <Flex grow={false} className="other-impacts">
                        <Space size={[8, 0]}>
                            <Popover
                                content={<DeltaTvImpact deltaTvImpacts={solution.solutionImpact?.deltaTvImpacts} />}
                                placement={'bottom'}>
                                <Button>TFVs</Button>
                            </Popover>
                            <Popover
                                content={
                                    <DeltaMeasuresImpact
                                        solutionMeasures={solution.measures.map(m => m.measureId.id)}
                                        deltaRegulations={solution.solutionImpact?.deltaRegulations}
                                    />
                                }
                                placement={'bottom'}>
                                <Button>Measures</Button>
                            </Popover>
                        </Space>
                    </Flex>
                </Flex>

                <Tabs
                    activeKey={this.state.selectedFMP}
                    type="card"
                    onTabClick={(key: string) =>
                        this.setState({
                            selectedFMP: key,
                            selectedMeasure: this.getMeasureForFmp(solution, key),
                        })
                    }
                    items={impactedFmps.map(fmp => ({ key: fmp, label: fmp, children: this.renderTvImpact(solution) }))}
                />
            </Flex>
        );
    };

    private renderTvImpact = (solution: DetailedSolution) => {
        const measure = solution.measures.find(m => m.measureId.id === this.state.selectedMeasure);
        const measureImpact = measure?.measureId?.id && solution.solutionImpact?.measureImpacts[measure.measureId.id];

        const regulation = measure && MeasureUtils.isRegulationOrMcdmOnly(measure) ? measure : null;
        const regulationImpact =
            measure && MeasureUtils.isRegulationOrMcdmOnly(measure) ? (measureImpact as RegulationImpact) : null;
        const monitoredTv =
            regulation &&
            solution.solutionImpact?.tvImpact?.find(
                tvImpact => tvImpact.tvId === MeasureUtils.getTvLocation(regulation).id,
            )?.monitoredTv;
        const rerouting = measure && MeasureUtils.isRerouting(measure) ? measure : null;
        const reroutingImpact =
            measure && MeasureUtils.isRerouting(measure) ? (measureImpact as ReroutingImpact) : null;

        const isReadOnly = AuthorisationUtils.isFmpUser() || SolutionUtils.isInitialSolution(solution);
        return (
            <Flex
                direction="vertical"
                className={`${this.state.coordination?.state !== CoordinationState.ONGOING ? 'disabled' : ''}`}>
                <Flex direction="vertical">
                    <TvLoadImpact solution={solution} />
                    <SolutionMeasures
                        solution={solution}
                        selectedMeasure={measure}
                        onSelectedMeasureChange={measure => this.setState({ selectedMeasure: measure.measureId.id })}
                    />
                    <ConditionalRender condition={Utils.isExists(regulation)}>
                        <RegulationImpactPanel
                            regulation={regulation}
                            regulationImpact={regulationImpact}
                            onRegulationEdited={this.handleMeasureEdited}
                            readonly={isReadOnly}
                            monitoredTv={monitoredTv}
                        />
                    </ConditionalRender>
                    <ConditionalRender condition={Utils.isExists(rerouting)}>
                        <ReroutingImpactPanel
                            rerouting={rerouting}
                            reroutingImpact={reroutingImpact}
                            readonly={isReadOnly}
                        />
                    </ConditionalRender>
                </Flex>
            </Flex>
        );
    };
}

type CoordinationPanelButtonsProps = {
    onApplySolution: () => void;
    applySolutionDisabled: boolean;
    onUpdateSolution: () => void;
    onResetCoordination: () => void;
    onCancelCoordination: () => void;
    cancelCoordinationDisabled: boolean;
    onClose: () => void;
    isOutdated: boolean;
};

class CoordinationPanelButtons extends PureComponent<CoordinationPanelButtonsProps> {
    render() {
        return (
            <Flex style={{ alignSelf: 'flex-end', padding: '0.5em' }} size="2em">
                <Popconfirm
                    title={'Cancel the coordination ?'}
                    okText={'Yes'}
                    cancelText={'No'}
                    onConfirm={this.props.onCancelCoordination}>
                    <Button style={{ marginRight: '1em' }} disabled={this.props.cancelCoordinationDisabled}>
                        CANCEL COORDINATION
                    </Button>
                </Popconfirm>
                <ConditionalRender condition={!this.props.isOutdated}>
                    <Button disabled={this.props.applySolutionDisabled} onClick={this.props.onApplySolution}>
                        APPLY
                    </Button>
                </ConditionalRender>
                <ConditionalRender condition={this.props.isOutdated}>
                    <Button onClick={this.props.onResetCoordination} style={{ marginRight: '1em' }}>
                        RESET
                    </Button>
                    <Button onClick={this.props.onUpdateSolution}>UPDATE IMPACT</Button>
                </ConditionalRender>
            </Flex>
        );
    }
}
